// @ts-nocheck
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import {
  DynamicValue, Element, ElementFactory,
} from '@fulcrumapp/fulcrum-core';
import { sortBy } from 'lodash';
import Box from '@mui/material/Box';
import TaskLinkField from './task-link-field';
import { LinkedRecord } from '../../shared/material_components/linked-cell';
import useOrgApps from './use-org-apps';
import appLinkSchema from './app-link-field-schema';
import { createButtonLabel } from '../tasks.helper';
import { PickerItemHandler } from '../../editor/fields/helpers/editor-fields-helper';
import AppItemLink from './app-item-link';
import { Instruction } from '../../hooks/use-form-fields';
import { RestApiForm } from '../types';
import { useGetUserPermissionsIdsQuery } from '../../services/user-permissions.service';
import ChoiceField, { IValue } from '../../editor/fields/choice-field';

interface Props {
  value: DynamicValue;
  onChange: (value: DynamicValue) => void;
  hidden?: boolean;
  disabled?: boolean
  onSelectField: (
    record?: any,
    formData?: any,
    shouldLinkToParent?: boolean,
    isAppLink?: boolean,
    specialInstructions?: Instruction[],
  ) => void,
  pickerItemHandler: PickerItemHandler,
  apiToken?: string,
  objectId?: any,
  requestType?: string,
}

const AppLinkField = ({
  value,
  onChange,
  hidden = false,
  pickerItemHandler,
  onSelectField,
  disabled,
  apiToken,
  objectId,
  requestType,
}: Props) => {
  const accessibleForms = useOrgApps();

  const { data: userPermissions } = useGetUserPermissionsIdsQuery({
    apiToken, objectId, requestType,
  });

  const formsChoices = sortBy(accessibleForms.map(({ id, name }) => {
    if (userPermissions && !userPermissions.userAccessibleFormIds?.includes(id)) {
      return {
        value: id,
        label: name,
        disable: true,
        disableText: 'no access',
        tooltipText: 'Add assignee to this app to enable',
      };
    }
    return { value: id, label: name };
  }), 'label');
  const appLinkValue = value.items[0] || value.createNewItem();
  const parsedAppLinkValue = appLinkValue.toJSON();
  const [appLinkField, setAppLinkField] = useState(appLinkValue);

  useEffect(() => {
    const appLinkVals = appLinkField;
    const choiceField = appLinkVals.elements.find((el: Element) => el.type === 'ChoiceField');
    const choiceFieldKey = choiceField?.key;
    if (!choiceFieldKey) {
      const createdChoiceField = ElementFactory.create(null, {
        ...({ ...appLinkSchema.ChoiceField, choices: formsChoices }),
        ...{},
      });
      appLinkVals.elements.push(createdChoiceField);
    }
    const recordLinkFieldKey = appLinkVals.elements.find((el: Element) => el.type === 'RecordLinkField')?.key;
    if (!recordLinkFieldKey) {
      let formId = null;
      if (choiceField?.choice_values) {
        [formId] = choiceField.choice_values;
      }
      const createdRecordFieldLink = ElementFactory.create(null, {
        ...({ ...appLinkSchema.RecordLinkField, form_id: formId }),
        ...{},
      });
      appLinkVals.elements.push(createdRecordFieldLink);
    }
    if (!recordLinkFieldKey || !choiceFieldKey) {
      setAppLinkField(appLinkVals);
    }
  }, [accessibleForms]);

  const showRecordLinkField = !!parsedAppLinkValue?.values?.ccc3?.choice_values.length;

  const handleChange = ({ key, updatedValue, updatedElement }) => {
    const elements = appLinkField.elements
      .map((element) => {
        const isRecordLinkField = appLinkSchema.RecordLinkField.type === element.type;
        const isUpdatingChoiceField = updatedElement.type === appLinkSchema.ChoiceField.type;
        const isChoiceField = appLinkSchema.ChoiceField.type === element.type;

        if (isRecordLinkField && isUpdatingChoiceField) {
          const [selectedForm] = updatedValue?.choice_values;
          if (selectedForm) {
            return { ...element.toJSON(), ...appLinkSchema.RecordLinkField, form_id: selectedForm };
          }
        }
        if (key === element.key) {
          return { ...element.toJSON(), ...updatedElement };
        }
        if (isChoiceField) {
          return { ...element.toJSON(), choices: formsChoices };
        }
        return { ...element.toJSON() };
      });

    const newElements = elements.reduce((result: Array<any>, element: any) => {
      if (element.type === appLinkSchema.ChoiceField.type
        || (element.form_id && element.form_id !== '')) {
        result.push(element);
      }
      return result;
    }, []);

    const newItemValue = {
      metadata: parsedAppLinkValue.metadata,
      elements: newElements,
      values: parsedAppLinkValue.values,
    };

    newItemValue.values[key] = updatedValue;
    onChange([newItemValue]);
  };

  const renderAppPicker = (element: Element) => (
    <ChoiceField
      hidden={element.hidden}
      multiple={element.multiple}
      label="Link an app"
      emptyLabel="No app selected"
      value={{ selectedValues: parsedAppLinkValue?.values?.ccc3?.choice_values || [] }}
      allowOther={false}
      disabled={parsedAppLinkValue.values?.ccc2?.length > 0}
      onChange={({ selectedValues }: IValue) => {
        handleChange({
          key: 'ccc3',
          updatedValue: { choice_values: selectedValues },
          updatedElement: {
            ...appLinkSchema.ChoiceField,
            choices: formsChoices || [],
            choice_values: selectedValues,
          },
        });
      }}
      choices={formsChoices || []}
    />
  );

  const renderRecordLinks = (element: Element) => {
    const [currentSelectedForm] = parsedAppLinkValue?.values?.ccc3?.choice_values || [];
    const specialInstructions = [
      { key: value.element.key, changes: [{ attributeName: 'disabled', value: 'true' }] },
    ];

    return (
      <>
        {showRecordLinkField
          && (
            <TaskLinkField
              key={element.key}
              hidden={!showRecordLinkField}
              title="Linked Record"
              sectionKey={element.dataName}
              taskIDs={parsedAppLinkValue.values?.ccc2?.map((rec) => rec.record_id) || []}
              listItem={(
                record: LinkedRecord,
                onClick: (linkedRecord: LinkedRecord, specialInstructions?: Instruction[]) => void,
                restForm?: RestApiForm,
              ) => (
                <AppItemLink
                  key={record.id}
                  record={record}
                  onClick={onClick}
                  restForm={restForm}
                  disabled={Boolean(disabled)}
                  specialInstructions={specialInstructions}
                />
              )}
              valuesForConditions={[]}
              onEditTask={(
                nextRecord,
                nextForm,
                shouldLinkToParent,
                _isAppLink,
                specialInstructs?: Instruction[],
              ) => onSelectField(nextRecord, nextForm, shouldLinkToParent, true, specialInstructs)}
              linkSelectedRecords={(recs) => {
                handleChange({
                  key: 'ccc2',
                  updatedValue: recs.reduce((result: Array<any>, raw: any) => {
                    if (raw.recordId) {
                      result.push({ record_id: raw.recordId });
                    }
                    return result;
                  }, []),
                  updatedElement: {
                    ...appLinkSchema.RecordLinkField,
                    form_id: currentSelectedForm,
                    allow_creating_records: true,
                    allow_existing_records: true,
                  },
                });
              }}
              linkedRecordFormID={currentSelectedForm}
              allowCreating={!parsedAppLinkValue.values?.ccc2?.length}
              isAppLink
              createLabel={createButtonLabel.record}
              pickerItemHandler={pickerItemHandler}
              disabled={disabled}
            />
          )}
      </>
    );
  };

  const renderField = (element: Element) => {
    switch (element?.type) {
      case 'ChoiceField':
        return renderAppPicker(element);
      case 'RecordLinkField':
        return renderRecordLinks(element);
      default:
        return <></>;
    }
  };

  return (
    <>
      {!hidden
        && appLinkField.elements.map((ele: Element, idx: number) => (
          <Box key={idx}>{renderField(ele)}</Box>
        ))}
    </>
  );
};

export default AppLinkField;
